import { Plus, Sun } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';

import { OverdueBand } from '@/components/tasks/OverdueBand';
import { TodaySchedule } from '@/components/tasks/TodaySchedule';
import { WaitingBand } from '@/components/tasks/WaitingBand';
import { InlineAdd } from '@/components/tasks/QuickAdd';
import { TaskGroup, TaskList } from '@/components/tasks/TaskList';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import type { ShellContext } from '@/components/layout/AppShell';
import { useCreateTask, useToday } from '@/hooks/useTasks';
import type { PriorityBand, TaskSummary } from '@/lib/api/types';
import { formatDayLong } from '@/lib/dates';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { BAND_LABELS } from '@/lib/priority';
import { useTaskActions } from '@/routes/useTaskActions';

/** Spec §18: three bands, so Today stays scannable rather than a flat wall. */
const BANDS: PriorityBand[] = ['MUST_DO', 'NORMAL', 'IF_THERE_IS_TIME'];

export function TodayRoute() {
  const t = useT();
  const { currentUser, openTask, openQuickAdd } = useOutletContext<ShellContext>();
  const { data, isPending, error, refetch } = useToday();
  const actions = useTaskActions(openTask);
  const createTask = useCreateTask();

  if (isPending)
    return (
      <Page>
        <LoadingState />
      </Page>
    );
  if (error)
    return (
      <Page>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Page>
    );
  if (!data) return null;

  const grouped = groupByBand(data.today);
  const openToday = data.today.filter((task) => task.status !== 'COMPLETED');
  const nothingAtAll = openToday.length === 0 && data.overdue.length === 0;

  return (
    <Page>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight text-content sm:text-4xl">
            {t('nav.today')}
          </h1>
          <p className="mt-1 text-lg font-medium text-accent">
            {formatDayLong(data.date)}
          </p>
        </div>
        <ProgressRing
          completed={data.today.filter((task) => task.status === 'COMPLETED').length}
          total={data.today.length}
        />
      </header>

      <QuickAddBar onOpen={openQuickAdd} />

      {/* What the day already contains, before deciding what else fits in it.
          Renders nothing when there is no calendar connected or nothing on it. */}
      <TodaySchedule />

      {/* Waiting on other people, above your own list: work you handed over
          leaves your list entirely, and a screen you have to remember to open
          is no reminder at all. */}
      <WaitingBand onOpen={openTask} />

      {/* Overdue first, always. Opening Today cannot avoid showing what slipped. */}
      <OverdueBand
        tasks={data.overdue}
        today={data.date}
        currentUserId={currentUser.id}
        actions={actions}
      />

      {nothingAtAll ? (
        <EmptyState
          icon={<Sun className="h-5 w-5" />}
          title={t('today.empty.title')}
          hint={t('today.empty.hint')}
        />
      ) : (
        BANDS.map((band) => {
          // A confirmed task leaves the day and lives in Completed — the
          // pending stage is the visible safety net, and once the user says
          // "finish" the row goes where finished work goes. The payload still
          // carries completed rows (the macOS ring and the widget count them);
          // the list simply does not show them.
          const tasks = grouped[band].filter((task) => task.status !== 'COMPLETED');
          if (tasks.length === 0) return null;
          return (
            <TaskGroup key={band} title={t(BAND_LABELS[band])} count={tasks.length}>
              <TaskList
                tasks={tasks}
                today={data.date}
                currentUserId={currentUser.id}
                actions={actions}
                showOpenSubtasks
              />
            </TaskGroup>
          );
        })
      )}

      <div className="mt-2">
        <InlineAdd
          isPending={createTask.isPending}
          onCreate={(input) => createTask.mutate({ ...input, schedule: 'today' })}
        />
      </div>
    </Page>
  );
}

function groupByBand(tasks: TaskSummary[]): Record<PriorityBand, TaskSummary[]> {
  const grouped: Record<PriorityBand, TaskSummary[]> = {
    MUST_DO: [],
    NORMAL: [],
    IF_THERE_IS_TIME: [],
  };
  for (const task of tasks) grouped[task.priority_band].push(task);
  return grouped;
}

export function Page({ children }: { children: React.ReactNode }) {
  // Aligned left, not centred. A 768px column floating in the middle of a
  // 1700px screen reads as a page that does not know how wide it is; against
  // the sidebar it reads as a column. It still stops growing — a task row a
  // metre wide is no easier to scan — but it stops later on a big screen,
  // where the room is real.
  //
  // 32px of padding either side costs a sixth of a phone screen, so it is
  // spent only where there is room for it.
  return (
    <div className="max-w-3xl px-4 py-5 sm:px-8 sm:py-8 2xl:max-w-5xl">{children}</div>
  );
}

/**
 * "N of M done", as the ring from the macOS design.
 *
 * The payload keeps completed-today rows precisely so this can be computed —
 * the list below hides them, but the day's progress is the whole point of
 * having finished anything.
 */
function ProgressRing({ completed, total }: { completed: number; total: number }) {
  const t = useT();
  if (total === 0) return null;
  const radius = 26;
  const circumference = 2 * Math.PI * radius;
  const fraction = completed / total;

  return (
    <div className="flex items-center gap-3">
      <svg width="64" height="64" viewBox="0 0 64 64" aria-hidden>
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          strokeWidth="5"
          className="stroke-border"
        />
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          strokeWidth="5"
          strokeLinecap="round"
          className="stroke-accent transition-[stroke-dashoffset] duration-500"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - fraction)}
          transform="rotate(-90 32 32)"
        />
      </svg>
      <div className="text-sm leading-tight">
        <span className="font-semibold text-content">
          {completed}
          <span className="font-normal text-content-muted"> / {total}</span>
        </span>
        <p className="text-content-subtle">{t('today.completedWord')}</p>
      </div>
    </div>
  );
}

/** The prominent capture bar from the design — one click into Quick Add. */
function QuickAddBar({ onOpen }: { onOpen: () => void }) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'mb-6 flex w-full items-center gap-3 rounded-xl bg-surface px-4 py-3',
        'text-left shadow-sm ring-1 ring-border transition-surface',
        'hover:ring-accent/40',
      )}
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-white">
        <Plus className="h-4 w-4" aria-hidden />
      </span>
      <span className="flex-1 text-sm text-content-subtle">{t('quickAdd.title')}</span>
      <kbd className="rounded border border-border px-1.5 py-0.5 text-[10px] text-content-subtle">
        N
      </kbd>
    </button>
  );
}
